import { Component } from '@theme/component';

function visible(element) {
  return element instanceof HTMLElement && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
}
function intersects(a, b) { return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top; }

/** @extends {Component<{launcher:HTMLAnchorElement,dialog:HTMLDialogElement,human:HTMLAnchorElement,welcome:HTMLAnchorElement,chat:HTMLButtonElement}>} */
class Assistance extends Component {
  #listeners;
  #observer;
  #frame = 0;
  connectedCallback() { super.connectedCallback(); this.#bind(); }
  updatedCallback() { super.updatedCallback(); this.#bind(); }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort(); this.#observer?.disconnect(); cancelAnimationFrame(this.#frame);
    document.body.removeAttribute('data-bp-help-ready'); document.body.removeAttribute('data-bp-floating-busy');
  }
  #bind() {
    this.#listeners?.abort(); this.#observer?.disconnect(); cancelAnimationFrame(this.#frame);
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    const schedule = () => {
      if (this.#frame) return;
      this.#frame = requestAnimationFrame(() => { this.#frame = 0; this.#update(); });
    };
    for (const event of ['scroll','resize']) window.addEventListener(event, schedule, { signal, passive: true });
    window.visualViewport?.addEventListener('resize', schedule, { signal, passive: true });
    for (const event of ['focusin','focusout','visitorConsentCollected','shopify-chat:close','theme-drawer:open','theme-drawer:close']) document.addEventListener(event, schedule, { signal });
    this.refs.dialog.addEventListener('close', () => {
      this.refs.launcher.setAttribute('aria-expanded', 'false');
      this.#update();
      this.refs.launcher.focus({ preventScroll: true });
    }, { signal });
    this.#observer = new MutationObserver(schedule);
    this.#observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open','data-stuck','class','aria-hidden'] });
    const offer = document.querySelector('.bp-subscription-entry');
    this.refs.welcome.hidden = !offer;
    if (offer) this.refs.welcome.textContent = offer.textContent;
    this.prepareHumanLink();
    this.hidden = false;
    document.body.setAttribute('data-bp-help-ready', '');
    this.#update();
  }
  #update() {
    const launcher = this.refs.launcher;
    const cookiePanel = ['shopify-pc__banner','shopify-pc__prefs'].some((id) => visible(document.getElementById(id)));
    const cookiePending = cookiePanel;
    const chatOpen = Boolean(document.querySelector('shopify-chat[open]'));
    const modal = [...document.querySelectorAll('dialog[open], [aria-modal="true"], theme-drawer[open]')].some(visible);
    const editing = Boolean(document.activeElement?.closest('input:not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"]):not([type="hidden"]):not([type="range"]),textarea,select,[contenteditable="true"]'));
    const keyboard = Boolean(innerWidth <= 749 && window.visualViewport && window.visualViewport.height < window.innerHeight * .75);
    document.body.toggleAttribute('data-bp-floating-busy', cookiePending || modal || chatOpen || editing || keyboard);
    launcher.hidden = cookiePending || modal || chatOpen || editing || keyboard || this.dataset.pageType === 'cart';
    if (launcher.hidden) return;
    const sticky = document.querySelector('.sticky-add-to-cart__bar[data-stuck="true"]');
    const bounds = visible(sticky) ? sticky.getBoundingClientRect() : null;
    const preview = document.getElementById('PBarNextFrame');
    const previewBounds = visible(preview) ? preview.getBoundingClientRect() : null;
    const previewBottom = previewBounds && previewBounds.height < innerHeight / 2 && previewBounds.top < innerHeight && previewBounds.bottom >= innerHeight - 1 ? Math.ceil(innerHeight - previewBounds.top + 12) : 18;
    const stickyBottom = bounds && bounds.top < innerHeight && bounds.bottom > 0 ? Math.ceil(innerHeight - bounds.top + 12) : 18;
    const bottom = Math.max(previewBottom, stickyBottom);
    const value = `max(${bottom}px, env(safe-area-inset-bottom))`;
    if (this.style.getPropertyValue('--bp-assistance-bottom') !== value) this.style.setProperty('--bp-assistance-bottom', value);
    const rect = launcher.getBoundingClientRect();
    if (innerWidth <= 749) {
      const protectedElements = document.querySelectorAll('.product-details product-price,.product-details variant-picker,.product-details .product-form-buttons,.product-details bp-size-alternatives');
      launcher.hidden = [...protectedElements].some((element) => visible(element) && intersects(rect, element.getBoundingClientRect()));
    }
  }
  /** @param {MouseEvent} event */
  openHelp(event) {
    event.preventDefault();
    this.prepareHumanLink();
    if (!this.refs.dialog.open) this.refs.dialog.showModal();
    this.refs.launcher.setAttribute('aria-expanded', 'true');
    this.#update();
  }
  closeHelp() { if (this.refs.dialog.open) this.refs.dialog.close(); }
  /** @param {MouseEvent} event */
  closeOnBackdrop(event) {
    const dialog = this.refs.dialog;
    if (event.target !== dialog) return;
    const r = dialog.getBoundingClientRect();
    if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.closeHelp();
  }
  /** @param {MouseEvent} event */
  openSizeGuide(event) {
    const help = document.querySelector('.product-details bp-size-help');
    this.closeHelp();
    if (help?.querySelector('dialog') && typeof help.openGuide === 'function') help.openGuide(event);
  }
  prepareHumanLink() {
    const help = document.querySelector('.product-details bp-size-help');
    if (typeof help?.prepareMessage === 'function') {
      help.prepareMessage();
      const link = help.querySelector('a.bp-size-help__whatsapp');
      if (link instanceof HTMLAnchorElement) { this.refs.human.href = link.href; return; }
    }
    const lines = [this.dataset.greeting];
    if (this.dataset.productTitle) {
      lines.push(this.dataset.productTitle);
      if (this.dataset.code) lines.push(`${this.dataset.referenceLabel}: ${this.dataset.code}`);
      lines.push(this.dataset.productUrl);
    }
    const url = new URL('https://wa.me/34654652368');
    url.searchParams.set('text', lines.join('\n'));
    this.refs.human.href = url.href;
  }
  openChat() { this.closeHelp(); document.dispatchEvent(new CustomEvent('bp:open-store-chat')); }
}
if (!customElements.get('bp-assistance')) customElements.define('bp-assistance', Assistance);
